import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft } from 'lucide-react';
import { VERIFY_OTP_URL, RESEND_OTP_URL } from '../../config/api';
import '../Auth/Auth.css';

interface OTPVerificationProps {
  email: string;
  onVerified: () => void;
  onBack: () => void;
}

const OTPVerification: React.FC<OTPVerificationProps> = ({ email, onVerified, onBack }) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isResending, setIsResending] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleOTPChange = (index: number, value: string) => {
    if (value.length > 1) return;
    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    if (error) setError('');
    if (successMsg) setSuccessMsg('');
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData.length === 6) {
      setOtp(pastedData.split(''));
      inputRefs.current[5]?.focus();
    }
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();

    const otpString = otp.join('');
    if (otpString.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(VERIFY_OTP_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp: otpString }),
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        // Persist auth tokens so the user is logged in immediately after signup
        if (data.access_token) {
          localStorage.setItem('authToken', data.access_token);
          localStorage.setItem('refreshToken', data.refresh_token || '');
          localStorage.setItem('tokenType', data.token_type || 'bearer');
          if (data.user) {
            localStorage.setItem('userid', String(data.user.user_id));
            localStorage.setItem('userEmail', data.user.email || email);
            const roles = Array.isArray(data.user.role) ? data.user.role : (data.user.role ? [data.user.role] : []);
            localStorage.setItem('userRole', JSON.stringify(roles));
            localStorage.setItem('profileCompleted', 'false');
          }
        }
        onVerified();
      } else {
        setError(data.message || data.detail || 'Invalid verification code. Please try again.');
        setOtp(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendCooldown > 0 || isResending) return;

    setIsResending(true);
    setError('');
    setSuccessMsg('');

    try {
      // Resend OTP takes email as a query param (POST with no body)
      const response = await fetch(RESEND_OTP_URL(email), {
        method: 'POST',
        headers: { accept: 'application/json' },
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        setResendCooldown(60);
        setOtp(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
        setSuccessMsg(data.message || 'New verification code sent to your email.');
      } else {
        setError(data.message || 'Failed to resend code. Please try again.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="auth-container otp-container">
      <button className="back-button" onClick={onBack} type="button">
        <ArrowLeft size={20} />
        Back
      </button>

      <h2 className="auth-title">Verify Your Email</h2>

      <p className="otp-description">
        We've sent a 6-digit verification code to<br />
        <strong>{email}</strong>
      </p>

      <form className="auth-form otp-form" onSubmit={handleVerifyOTP}>
        <div className="otp-inputs">
          {otp.map((digit, index) => (
            <input
              key={index}
              ref={(el) => { inputRefs.current[index] = el; }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleOTPChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={index === 0 ? handlePaste : undefined}
              className={`otp-input ${error ? 'error' : ''}`}
              autoComplete="off"
            />
          ))}
        </div>

        {error && <div className="error-message">{error}</div>}
        {successMsg && <div className="success-message">{successMsg}</div>}

        <button
          type="submit"
          className="auth-button"
          disabled={isLoading || otp.join('').length !== 6}
        >
          {isLoading ? 'Verifying...' : 'Verify Code'}
        </button>
      </form>

      <div className="resend-section">
        <p>Didn't receive the code?</p>
        <button
          type="button"
          className="resend-button"
          onClick={handleResendOTP}
          disabled={resendCooldown > 0 || isResending}
        >
          {isResending
            ? 'Sending...'
            : resendCooldown > 0
              ? `Resend in ${resendCooldown}s`
              : 'Resend Code'
          }
        </button>
      </div>
    </div>
  );
};

export default OTPVerification;
